import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { trackConversion } from "../../lib/analytics";
import {
  webinar,
  isWebinarVisible,
  getWebinarTitle,
  getWebinarDateLabel,
} from "../../lib/webinar";

// Clave de descarte ligada a la identidad del evento: si publicas un webinar
// nuevo (otra fecha/URL) la barra vuelve a mostrarse aunque el usuario cerrara
// la anterior.
const dismissKey = () => `webinar_dismissed:${webinar.startsAt || webinar.meetupUrl}`;

// Rutas con layout propio, donde esta barra NO se pinta. La landing del kit y
// su confirmacion tienen su propia cabecera minima: la barra del evento se le
// montaba encima y, ademas, empuja el layout con --webinar-bar-height.
const HIDDEN_PREFIXES = ["/resources/ai-kit"];

/**
 * Barra de anuncio fina, fija arriba y visible en todo el sitio.
 * El CTA lleva directo al evento de Meetup (el RSVP se hace allí).
 * Es descartable (recuerda el cierre en localStorage) y se oculta sola cuando
 * pasa el webinar (ver lib/webinar.js).
 */
const WebinarTopBar = () => {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const barRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const hiddenHere = HIDDEN_PREFIXES.some((prefix) => pathname.startsWith(prefix));

  // Decide visibilidad en cliente (respeta el descarte previo del usuario).
  useEffect(() => {
    if (hiddenHere || !isWebinarVisible()) {
      setVisible(false);
      return;
    }
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(dismissKey()) === "1";
    } catch (e) {
      /* localStorage no disponible -> se muestra */
    }
    setVisible(!dismissed);
  }, [hiddenHere]);

  // Mantiene sincronizado el offset de las cabeceras (que van fixed/absolute a
  // top:0) con la altura REAL de la barra, que puede variar al hacer wrap en
  // móvil. Publicamos la altura como variable CSS en <body> y añadimos la clase
  // has-webinar-bar (el CSS empuja .header__transparent y el header sticky).
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const body = document.body;

    if (!visible) {
      body.classList.remove("has-webinar-bar");
      body.style.removeProperty("--webinar-bar-height");
      return undefined;
    }

    body.classList.add("has-webinar-bar");

    const applyHeight = () => {
      const h = barRef.current ? barRef.current.offsetHeight : 0;
      if (h) body.style.setProperty("--webinar-bar-height", `${h}px`);
    };

    // La primera medición puede caer en un instante en el que el layout aún no
    // se ha estabilizado (fuentes/estilos), dando una altura errónea. Re-medimos
    // en los siguientes frames y cuando fuentes/recursos terminan de cargar.
    applyHeight();
    const raf1 = requestAnimationFrame(applyHeight);
    const raf2 = requestAnimationFrame(() =>
      requestAnimationFrame(applyHeight)
    );
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(applyHeight).catch(() => {});
    }
    window.addEventListener("load", applyHeight);
    window.addEventListener("resize", applyHeight);

    let ro;
    if (typeof ResizeObserver !== "undefined" && barRef.current) {
      ro = new ResizeObserver(applyHeight);
      ro.observe(barRef.current);
    }

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.removeEventListener("load", applyHeight);
      window.removeEventListener("resize", applyHeight);
      if (ro) ro.disconnect();
      body.classList.remove("has-webinar-bar");
      body.style.removeProperty("--webinar-bar-height");
    };
  }, [visible]);

  if (!visible) return null;

  const lang = i18n && i18n.language ? i18n.language : "es";
  const dateLabel = getWebinarDateLabel(lang);

  const handleDismiss = () => {
    try {
      localStorage.setItem(dismissKey(), "1");
    } catch (e) {
      /* ignore */
    }
    setVisible(false);
  };

  return (
    <div
      className="webinar-bar"
      ref={barRef}
      role="region"
      aria-label={t("webinar_aria_label")}
    >
      <a
        className="webinar-bar__inner"
        href={webinar.meetupUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackConversion("Webinar Bar CTA")}
      >
        <span className="webinar-bar__label">
          <span className="webinar-bar__dot" aria-hidden="true" />
          {t("webinar_live_label")}
        </span>
        <span className="webinar-bar__text">
          <strong className="webinar-bar__title">{getWebinarTitle(lang)}</strong>
          {dateLabel ? (
            <span className="webinar-bar__date">{dateLabel}</span>
          ) : null}
        </span>
        <span className="webinar-bar__cta">{t("webinar_cta")}</span>
      </a>
      <button
        type="button"
        className="webinar-bar__close"
        aria-label={t("webinar_dismiss")}
        onClick={handleDismiss}
      >
        &times;
      </button>
    </div>
  );
};

export default WebinarTopBar;
